import React, { useCallback, useMemo } from 'react';
import { ReactSVG } from 'react-svg';
import classNames from 'classnames';
import { useIsMobile } from 'Hooks/Common';
import { SignActionLabel } from 'Interfaces/Document';

import CheckCircle from 'Assets/images/icons/check-circle.svg';
import LastStepIcon from 'Assets/images/icons/finish-step-icon.svg';
import FirstStepIcon from 'Assets/images/icons/first-step-icon.svg';
import MiddleStepIcon from 'Assets/images/icons/middle-step-icon.svg';
import MobileLastStepIcon from 'Assets/images/icons/mobile-finish-step-icon.svg';
import MobileFirstStepIcon from 'Assets/images/icons/mobile-first-step-icon.svg';
import MobileMiddleStepIcon from 'Assets/images/icons/mobile-middle-step-icon.svg';

export interface ActionStepBarProps {
  onChoose: (step: number) => void;
  actionType?: SignActionLabel;
}

const ActionStepBar = ({ onChoose, actionType }: ActionStepBarProps) => {
  const isMobile = useIsMobile();

  const options = useMemo(
    () => [
      {
        step: 1,
        icon: FirstStepIcon,
        mobileIcon: MobileFirstStepIcon,
        title: 'Upload the Files',
        mobileTitle: 'Upload',
        isFirst: true,
        isSelected: false,
        hasFinished: true,
      },
      {
        step: 2,
        icon: MiddleStepIcon,
        mobileIcon: MobileMiddleStepIcon,
        title: 'Choose the Signers',
        mobileTitle: 'Action',
        isFirst: false,
        isSelected: false,
        hasFinished: true,
      },
      {
        step: 3,
        icon: MiddleStepIcon,
        mobileIcon: MobileMiddleStepIcon,
        title: actionType,
        mobileTitle: 'Sign',
        isFirst: false,
        isSelected: false,
        hasFinished: true,
      },
      {
        step: 4,
        icon: LastStepIcon,
        mobileIcon: MobileLastStepIcon,
        title: 'Finish',
        mobileTitle: 'Finish',
        isFirst: false,
        isSelected: true,
        hasFinished: false,
      },
    ],
    [actionType],
  );

  const handleStepChoose = useCallback(
    (step: number) => {
      onChoose(step);
    },
    [onChoose],
  );

  return (
    <div className={classNames('actionStepBar__container', { mobile: isMobile })}>
      {options.map(option => (
        <button
          className={classNames('actionStepBar__item', { mobile: isMobile })}
          type="button"
          onClick={() => handleStepChoose(option.step)}
          key={option.step}
        >
          <ReactSVG src={isMobile ? option.mobileIcon : option.icon} wrapper="span" />
          {!isMobile && (
            <span
              className={classNames('actionStepBar__item--stepNumber', {
                first: option.isFirst,
                selected: option.isSelected,
              })}
            >
              {option.step}.
            </span>
          )}
          <span
            className={classNames('actionStepBar__item--text', {
              first: option.isFirst,
              selected: option.isSelected,
              mobile: isMobile,
            })}
          >
            {isMobile ? option.mobileTitle : option.title}
          </span>
          {option.hasFinished && !isMobile && (
            <ReactSVG
              className="actionStepBar__item--checkIcon"
              src={CheckCircle}
              wrapper="span"
            />
          )}
        </button>
      ))}
    </div>
  );
};

export default ActionStepBar;
